import Phaser from "phaser";
import type { WorldState } from "@/core";
import type { AppMode, GameApp } from "../app/GameApp";
import { REGISTRY_KEYS } from "../app/GameApp";

const MODE_SCENE: Record<Exclude<AppMode, "boot" | "map">, string> = {
  town: "TownScene",
  characterCreator: "CharacterCreatorScene",
  battle: "BattleScene",
  event: "EventScene",
  vote: "VotingOverlay",
  cutscene: "CutsceneScene",
  forge: "ForgeOverlay",
  tavern: "TavernOverlay",
  guild: "GuildOverlay",
  temple: "TempleOverlay",
  equipment: "EquipmentOverlay",
  choice: "ChoiceOverlay",
};

const FULLSCREEN_MODES = new Set<AppMode>(["town", "characterCreator", "battle", "event"]);
const OVERLAY_MODES = new Set<AppMode>(["forge", "tavern", "guild", "temple", "equipment", "choice", "vote", "cutscene"]);
const HIDES_MAP_OVERLAYS = new Set<AppMode>(["cutscene"]);

const MAP_OVERLAYS = ["MinimapScene", "HudScene"] as const;
const FULLSCREEN_SCENES = Array.from(FULLSCREEN_MODES).map((m) => MODE_SCENE[m as Exclude<AppMode, "boot" | "map">]);
const OVERLAY_SCENES = Array.from(OVERLAY_MODES).map((m) => MODE_SCENE[m as Exclude<AppMode, "boot" | "map">]);

export class BootScene extends Phaser.Scene {
  private app!: GameApp;

  constructor() {
    super({ key: "BootScene" });
  }

  create() {
    this.app = this.game.registry.get("app") as GameApp;

    const handleInitial = (state: WorldState) => {
      if (!state.hero.classId || !state.hero.domainId) {
        this.app.openCharacterCreator((classId, domainId) => {
          this.app.applyCharacterCreation(classId, domainId);
          this.app.backToMap();
        });
      } else {
        this.app.backToMap();
      }
    };

    let handled = false;
    const tryHandle = (state: WorldState) => {
      if (handled) return;
      handled = true;
      handleInitial(state);
    };

    const existing = this.app.getWorldState();
    const unsubWorld = this.app.onWorldState(tryHandle);
    if (existing) tryHandle(existing);

    const onMode = (_p: unknown, mode: AppMode) => this.applyMode(mode);
    this.registry.events.on(`changedata-${REGISTRY_KEYS.currentMode}`, onMode);

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      unsubWorld();
      this.registry.events.off(`changedata-${REGISTRY_KEYS.currentMode}`, onMode);
    });
  }

  private applyMode(mode: AppMode) {
    if (mode === "boot") return;

    if (mode === "map") {
      for (const key of [...FULLSCREEN_SCENES, ...OVERLAY_SCENES]) this.stopIfActive(key);
      if (!this.scene.isActive("MapScene")) this.scene.run("MapScene");
      else if (this.scene.isPaused("MapScene")) this.scene.resume("MapScene");
      for (const k of MAP_OVERLAYS) {
        if (this.scene.isSleeping(k)) this.scene.wake(k);
        else if (!this.scene.isActive(k)) this.scene.run(k);
        this.scene.bringToTop(k);
      }
      return;
    }

    const target = MODE_SCENE[mode];

    if (OVERLAY_MODES.has(mode)) {
      if (this.scene.isActive("MapScene")) this.scene.pause("MapScene");
      if (this.scene.isActive("TownScene")) this.scene.pause("TownScene");
      for (const key of FULLSCREEN_SCENES) {
        if (key === "TownScene") continue;
        this.stopIfActive(key);
      }
      for (const key of OVERLAY_SCENES) {
        if (key !== target) this.stopIfActive(key);
      }
      if (HIDES_MAP_OVERLAYS.has(mode)) {
        for (const k of MAP_OVERLAYS) {
          if (this.scene.isActive(k)) this.scene.sleep(k);
        }
      }
      if (!this.scene.isActive(target)) this.scene.run(target);
      this.scene.bringToTop(target);
      return;
    }

    if (this.scene.isActive("MapScene")) this.scene.pause("MapScene");
    for (const k of MAP_OVERLAYS) {
      if (this.scene.isActive(k)) this.scene.sleep(k);
    }
    for (const key of FULLSCREEN_SCENES) {
      if (key !== target) this.stopIfActive(key);
    }
    for (const key of OVERLAY_SCENES) this.stopIfActive(key);

    if (!this.scene.isActive(target)) this.scene.run(target);
    this.scene.bringToTop(target);
  }

  private stopIfActive(key: string) {
    if (this.scene.isActive(key) || this.scene.isPaused(key) || this.scene.isSleeping(key)) {
      this.scene.stop(key);
    }
  }
}
